import {clsx} from 'clsx'
import type {VideoHTMLAttributes} from 'react'

import {AutoPlayVideoButton} from './AutoPlayVideoButton'
import {
  type AutoPlayVideoAnalyticsOptions,
  type AutoPlayVideoCopy,
  useAutoPlayVideo,
  type UseAutoPlayVideoOptions,
} from './useAutoPlayVideo'

import Styles from './AutoPlayVideo.module.css'

export interface AutoPlayVideoProps extends VideoHTMLAttributes<HTMLVideoElement> {
  src: string
  poster: string
  /** Localized button copy. Falls back to translated defaults when omitted. */
  copy?: AutoPlayVideoCopy
  /** MIME type for the `<source>` element. */
  mediaType?: string
  /** Class applied to the wrapping element. */
  className?: string
  /** Class applied to the `<video>` element, in addition to the default styling. */
  videoClassName?: string
  /** Class applied to the play/pause button, in addition to the default styling. */
  buttonClassName?: string
  /** Passthrough for the `enableIntersectionObserver` hook option. */
  enableIntersectionObserver?: UseAutoPlayVideoOptions['enableIntersectionObserver']
  /**
   * Analytics configuration. When provided, the button emits a `data-analytics-event`
   * built with swp-core's `getAnalyticsEvent` for the current action.
   */
  analytics?: AutoPlayVideoAnalyticsOptions
  /**
   * Escape hatch to fully control the button's analytics attributes. Receives the
   * current action (`play`, `pause`, or `replay`) and takes precedence over `analytics`.
   */
  getButtonAnalyticsAttributes?: (action: string) => Record<string, unknown>
}

export function AutoPlayVideo({
  src,
  poster,
  copy,
  mediaType = 'video/mp4; codecs=avc1.4d002a',
  className,
  videoClassName,
  buttonClassName,
  enableIntersectionObserver,
  analytics,
  getButtonAnalyticsAttributes,
  ...props
}: AutoPlayVideoProps) {
  const {
    registerVideo,
    buttonLabel,
    buttonPressed,
    buttonAriaLabel,
    buttonAnalyticsAttributes,
    toggle,
    onPlay,
    onPause,
    onEnded,
  } = useAutoPlayVideo({copy, enableIntersectionObserver, analytics, getButtonAnalyticsAttributes})

  return (
    <div className={clsx(Styles['AutoPlayVideo'], className)}>
      <video
        ref={registerVideo}
        playsInline
        muted
        preload="none"
        poster={poster}
        className={clsx(Styles['Video'], videoClassName)}
        {...props}
        onPlay={onPlay}
        onPause={onPause}
        onEnded={onEnded}
      >
        <source src={src} type={mediaType} />
      </video>

      <AutoPlayVideoButton
        className={clsx(Styles['PlayButton'], buttonClassName)}
        iconClassName={Styles['PlayButtonIcon']}
        pressed={buttonPressed}
        label={buttonLabel}
        ariaLabel={buttonAriaLabel}
        onClick={toggle}
        analyticsAttributes={buttonAnalyticsAttributes}
      />
    </div>
  )
}

export default AutoPlayVideo
